/**
 * QuoteRequestStatusBadge — badge cohérent pour les 6 statuts Quote Requests.
 *
 * Mapping enum `app.quote_request_status` → tons brand alignés :
 *   - draft               → slate (préparation, aucun envoi)
 *   - sent                → amber (envoyé, en attente réponses)
 *   - partially_received  → brand-blue (au moins 1 recipient a répondu)
 *   - fully_received      → emerald (tous recipients ont répondu)
 *   - cancelled           → red (annulé broker)
 *   - expired             → slate (deadline dépassée)
 *
 * Pattern parité `ProjectStatusBadge` + `IntakeEventStatusBadge` (9.G.2).
 */

import { useTranslation } from "react-i18next";

import type { QuoteRequestStatus } from "@kollectivia/shared";

type Tone = "slate" | "amber" | "brand" | "emerald" | "red";

const TONE: Record<Tone, { bg: string; text: string; dot: string }> = {
  slate: { bg: "bg-slate-100", text: "text-slate-700", dot: "bg-slate-400" },
  amber: { bg: "bg-amber-50", text: "text-amber-700", dot: "bg-amber-500" },
  brand: { bg: "bg-brand-50", text: "text-brand-700", dot: "bg-brand-500" },
  emerald: {
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    dot: "bg-emerald-500",
  },
  red: { bg: "bg-red-50", text: "text-red-700", dot: "bg-red-500" },
};

function toneFor(status: QuoteRequestStatus): Tone {
  switch (status) {
    case "draft":
      return "slate";
    case "sent":
      return "amber";
    case "partially_received":
      return "brand";
    case "fully_received":
      return "emerald";
    case "cancelled":
      return "red";
    case "expired":
      return "slate";
    default:
      return "slate";
  }
}

type Props = {
  status: QuoteRequestStatus;
};

export function QuoteRequestStatusBadge({ status }: Props) {
  const { t } = useTranslation();
  const tone = TONE[toneFor(status)];
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full ${tone.bg} px-2.5 py-0.5 text-xs font-medium ${tone.text}`}
    >
      <span
        aria-hidden="true"
        className={`h-1.5 w-1.5 rounded-full ${tone.dot}`}
      />
      {t(`quote_requests.statuses.${status}`)}
    </span>
  );
}
